package structs.accelbyte.promo

import kotlinx.serialization.Serializable
import structs.accelbyte.common.AccelByteJson
import structs.accelbyte.common.AccelByteSerializable

/**
 * Operator-only request to mint a batch of single-use codes against an
 * AGS campaign.
 *
 * There are two ways to use this:
 * - Mint codes against an existing campaign: pass `existingCampaignId` and
 *   the outcome type that campaign already encodes.
 * - Create the campaign and the codes in one shot: pass
 *   `existingCampaignId = null`, set `outcomeType` to `SUBSCRIPTION_MONTHS`
 *   or `CREDITS_GRANT`, and the proxy picks the campaign item template from
 *   operator-side configuration (`globals.PromoConfig`) baked into the
 *   server-extend binary at packaging time.
 *
 * `subscriptionMonths` is required when `outcomeType = SUBSCRIPTION_MONTHS`
 *; `creditGrantAmount` is required when `outcomeType = CREDITS_GRANT`. The
 * proxy rejects requests where neither or both fields are set.
 *
 * @property existingCampaignId AGS campaign ID, or null to create a new
 *   campaign alongside the codes.
 * @property batchName AGS CodeCreate `batchName`; the server-extend proxy
 *   inherits AGS auto-batch-number behaviour.
 * @property codes The literal code strings the operator wants minted. The
 *   proxy issues one AGS `CodeCreate` call per string with `quantity = 1`,
 *   so each code is independently single-use.
 * @property outcomeType Which family of granted item this batch should
 *   produce. Ignored when `existingCampaignId` is set (the existing
 *   campaign already pins this).
 * @property subscriptionMonths Number of calendar months the granted item
 *   carries via `extraSubscriptionDays`.
 * @property creditGrantAmount Number of credits the granted item represents.
 */
@Serializable
data class CreatePromoCodeBatchRequest(
    val existingCampaignId: String? = null,
    val batchName: String,
    val codes: List<String>,
    val outcomeType: PromoOutcomeType,
    val subscriptionMonths: Int? = null,
    val creditGrantAmount: Int? = null
) : AccelByteSerializable {
    override fun toJsonString(): String = AccelByteJson.encodeToString(serializer(), this)
}

/**
 * Result of an operator-side `createPromoCodeBatch`.
 *
 * `createdCampaignId` carries the AGS campaign ID the codes were attached
 * to. When `existingCampaignId` was supplied the proxy echoes it back so
 * the operator's audit trail is consistent. `codeValues` mirrors the
 * request list with each entry's literal `codeValue`. AGS's
 * `CreateCodes` response only reports `numCreated` (an integer count),
 * not per-code IDs; if the operator later needs to disable or look up a
 * specific code they query AGS `QueryCodes` filtered on
 * `codeValue` from their own records.
 */
@Serializable
data class CreatePromoCodeBatchResponse(
    val createdCampaignId: String,
    val codeValues: List<PromoCodeCreatedEntry>
) : AccelByteSerializable {
    override fun toJsonString(): String = AccelByteJson.encodeToString(serializer(), this)
}

/**
 * Per-code row in the create-batch response.
 *
 * @property codeValue The literal code the operator typed.
 *
 * AGS does not expose a per-code ID at creation time -- only
 * `numCreated`. Operators can later query AGS `QueryCodes` filtered on
 * `codeValue` if they need to disable / audit a specific code.
 */
@Serializable
data class PromoCodeCreatedEntry(
    val codeValue: String
)
